import { Link } from 'react-router-dom'
import { Star, Users, Clock, Megaphone } from 'lucide-react'
import { Course } from '../../types/database'

interface CourseCardProps {
  course: Course
  relatedCampaign?: {
    id: string
    title: string
  }
}

export default function CourseCard({ course, relatedCampaign }: CourseCardProps) {
  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow duration-300 border border-neutral-200">
      {/* Imagen del curso */}
      <div className="aspect-video w-full bg-neutral-200 overflow-hidden">
        {course.image_url ? (
          <img
            src={course.image_url}
            alt={course.title}
            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-education-light text-white">
            <span className="text-4xl font-bold">{course.title.charAt(0)}</span>
          </div>
        )}
      </div>

      {/* Contenido de la tarjeta */}
      <div className="p-4">
        {/* Institución y rating */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-1 text-sm text-neutral-600">
            {course.instructor_name && (
              <span className="font-medium">{course.instructor_name}</span>
            )}
          </div>
          <div className="flex items-center space-x-1">
            <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
            <span className="text-sm font-semibold">{course.rating.toFixed(1)}</span>
            <span className="text-xs text-neutral-500">({course.total_students})</span>
          </div>
        </div>

        {/* Título del curso */}
        <Link to={`/cursos/${course.id}`}>
          <h3 className="font-heading font-semibold text-lg text-neutral-800 mb-2 hover:text-education transition-colors line-clamp-2">
            {course.title}
          </h3>
        </Link>

        {/* Tags temáticos */}
        <div className="flex flex-wrap gap-2 mb-3">
          {course.theme && (
            <span className="px-2 py-1 bg-education-light bg-opacity-10 text-education text-xs rounded-full">
              #{course.theme}
            </span>
          )}
          {course.level && (
            <span className="px-2 py-1 bg-neutral-100 text-neutral-600 text-xs rounded-full capitalize">
              {course.level}
            </span>
          )}
        </div>

        {/* Información adicional */}
        <div className="flex items-center justify-between text-sm text-neutral-600 mb-3">
          <div className="flex items-center space-x-1">
            <Clock className="w-4 h-4" />
            <span>{course.duration_hours}h</span>
          </div>
          <div className="flex items-center space-x-1">
            <Users className="w-4 h-4" />
            <span>{course.total_students} estudiantes</span>
          </div>
        </div>

        {/* Botón principal */}
        <Link to={`/cursos/${course.id}`}>
          <button className="w-full bg-education text-white py-2 rounded-lg font-medium hover:bg-education-dark transition-colors">
            Enrollarse
          </button>
        </Link>

        {/* Campaña relacionada (mini-card integrado) */}
        {relatedCampaign && (
          <div className="mt-3 p-3 bg-action-light bg-opacity-10 rounded-lg border border-action-light">
            <div className="flex items-start space-x-2">
              <Megaphone className="w-4 h-4 text-action mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-action mb-1">
                  ¡Campaña relacionada!
                </p>
                <p className="text-xs text-neutral-700 line-clamp-1">
                  {relatedCampaign.title}
                </p>
                <Link
                  to={`/campanas/${relatedCampaign.id}`}
                  className="inline-block mt-1"
                >
                  <button className="text-xs bg-action text-white px-3 py-1 rounded hover:bg-action-dark transition-colors">
                    Únete
                  </button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
